/**
 * HealthPass IndexedDB Database
 * Local offline storage for health data with encryption support
 */

import Dexie, { Table } from "dexie";

// Types for stored data
export interface StoredHealthRecord {
  id?: number;
  serverId?: string; // ID from backend
  userId: string;
  type: string;
  date: string;
  description: string;
  value?: string;
  unit?: string;
  attachments?: string[];
  metadata?: Record<string, unknown>;
  syncStatus: "synced" | "pending" | "conflict";
  lastModified: number;
  localOnly: boolean;
}

export interface StoredMedication {
  id?: number;
  serverId?: string;
  userId: string;
  name: string;
  dosage: string;
  frequency: string;
  prescribedBy?: string;
  startDate: string;
  endDate?: string;
  sideEffects?: string;
  instructions?: string;
  active: boolean;
  syncStatus: "synced" | "pending" | "conflict";
  lastModified: number;
  localOnly: boolean;
}

export interface StoredVaccination {
  id?: number;
  serverId?: string;
  userId: string;
  name: string;
  date: string;
  batchNumber?: string;
  administeredBy?: string;
  nextDueDate?: string;
  notes?: string;
  syncStatus: "synced" | "pending" | "conflict";
  lastModified: number;
  localOnly: boolean;
}

export interface StoredDocument {
  id?: number;
  serverId?: string;
  userId: string;
  title: string;
  type: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadDate: string;
  expiryDate?: string;
  encrypted: boolean;
  thumbnailUrl?: string;
  syncStatus: "synced" | "pending" | "conflict";
  lastModified: number;
  localOnly: boolean;
}

export interface StoredVitalSign {
  id?: number;
  serverId?: string;
  userId: string;
  type:
    | "blood_pressure"
    | "heart_rate"
    | "temperature"
    | "weight"
    | "height"
    | "bmi";
  value: number;
  unit: string;
  systolic?: number;
  diastolic?: number;
  recordedAt: string;
  notes?: string;
  syncStatus: "synced" | "pending" | "conflict";
  lastModified: number;
  localOnly: boolean;
}

export interface SyncQueue {
  id?: number;
  userId: string;
  operation: "create" | "update" | "delete";
  resource:
    | "health-record"
    | "medication"
    | "vaccination"
    | "document"
    | "vital-sign";
  resourceId: string;
  // The request body to replay. DELETE operations have none, which the type
  // now admits instead of callers passing `null` into a non-nullable field.
  data: Record<string, unknown> | null;
  endpoint: string;
  method: "POST" | "PUT" | "DELETE";
  attempts: number;
  maxAttempts: number;
  lastAttempt?: number;
  error?: string;
  createdAt: number;
}

export interface SyncMetadata {
  id?: number;
  userId: string;
  resource: string;
  lastSyncTime: number;
  syncDirection: "upload" | "download" | "bidirectional";
  status: "idle" | "syncing" | "error";
  errorMessage?: string;
}

export interface EncryptionKey {
  id?: number;
  userId: string;
  keyId: string;
  encryptedKey: string; // Key encrypted with user password
  salt: string;
  iv: string;
  createdAt: number;
}

/**
 * HealthPass Dexie Database
 */
export class HealthPassDB extends Dexie {
  // Tables
  healthRecords!: Table<StoredHealthRecord, number>;
  medications!: Table<StoredMedication, number>;
  vaccinations!: Table<StoredVaccination, number>;
  documents!: Table<StoredDocument, number>;
  vitalSigns!: Table<StoredVitalSign, number>;
  syncQueue!: Table<SyncQueue, number>;
  syncMetadata!: Table<SyncMetadata, number>;
  encryptionKeys!: Table<EncryptionKey, number>;

  constructor() {
    super("HealthPassDB");

    // Version 1 - Initial schema
    this.version(1).stores({
      healthRecords:
        "++id, serverId, userId, type, date, syncStatus, lastModified",
      medications:
        "++id, serverId, userId, name, active, syncStatus, lastModified",
      vaccinations:
        "++id, serverId, userId, name, date, syncStatus, lastModified",
      documents:
        "++id, serverId, userId, type, uploadDate, syncStatus, lastModified",
      vitalSigns:
        "++id, serverId, userId, type, recordedAt, syncStatus, lastModified",
      syncQueue: "++id, userId, resource, operation, createdAt",
      syncMetadata: "++id, userId, resource, lastSyncTime",
      encryptionKeys: "++id, userId, keyId",
    });

    // Hooks for automatic timestamp updates
    this.healthRecords.hook("creating", (_primKey, obj) => {
      obj.lastModified = Date.now();
      if (obj.syncStatus === undefined) {
        obj.syncStatus = "pending";
      }
      if (obj.localOnly === undefined) {
        obj.localOnly = true;
      }
    });

    this.healthRecords.hook(
      "updating",
      (mods: Partial<StoredHealthRecord>, _primKey, obj) => {
        mods.lastModified = Date.now();
        const record = obj as StoredHealthRecord;
        if (record && record.syncStatus === "synced" && !mods.syncStatus) {
          mods.syncStatus = "pending"; // Mark as pending when updated locally
        }
      },
    );

    // Similar hooks for other tables
    [
      this.medications,
      this.vaccinations,
      this.documents,
      this.vitalSigns,
    ].forEach((table) => {
      table.hook("creating" as any, (_primKey, obj: any) => {
        obj.lastModified = Date.now();
        if (obj.syncStatus === undefined) {
          obj.syncStatus = "pending";
        }
        if (obj.localOnly === undefined) {
          obj.localOnly = true;
        }
      });

      table.hook("updating" as any, (mods: any, _primKey: any, obj: any) => {
        mods.lastModified = Date.now();
        if (obj && obj.syncStatus === "synced" && !mods.syncStatus) {
          mods.syncStatus = "pending";
        }
      });
    });
  }

  /**
   * Clear all user data (for logout)
   */
  async clearUserData(userId: string): Promise<void> {
    await Promise.all([
      this.healthRecords.where("userId").equals(userId).delete(),
      this.medications.where("userId").equals(userId).delete(),
      this.vaccinations.where("userId").equals(userId).delete(),
      this.documents.where("userId").equals(userId).delete(),
      this.vitalSigns.where("userId").equals(userId).delete(),
      this.syncQueue.where("userId").equals(userId).delete(),
      this.syncMetadata.where("userId").equals(userId).delete(),
      this.encryptionKeys.where("userId").equals(userId).delete(),
    ]);
  }

  /**
   * Get sync statistics
   */
  async getSyncStats(userId: string) {
    const [
      pendingHealthRecords,
      pendingMedications,
      pendingVaccinations,
      pendingDocuments,
      pendingVitalSigns,
      queuedItems,
    ] = await Promise.all([
      this.healthRecords.where({ userId, syncStatus: "pending" }).count(),
      this.medications.where({ userId, syncStatus: "pending" }).count(),
      this.vaccinations.where({ userId, syncStatus: "pending" }).count(),
      this.documents.where({ userId, syncStatus: "pending" }).count(),
      this.vitalSigns.where({ userId, syncStatus: "pending" }).count(),
      this.syncQueue.where("userId").equals(userId).count(),
    ]);

    return {
      pending: {
        healthRecords: pendingHealthRecords,
        medications: pendingMedications,
        vaccinations: pendingVaccinations,
        documents: pendingDocuments,
        vitalSigns: pendingVitalSigns,
        total:
          pendingHealthRecords +
          pendingMedications +
          pendingVaccinations +
          pendingDocuments +
          pendingVitalSigns,
      },
      queuedOperations: queuedItems,
    };
  }
}

// Singleton instance
export const db = new HealthPassDB();

// Export for debugging
if (typeof window !== "undefined") {
  (window as Window & { healthPassDB?: HealthPassDB }).healthPassDB = db;
}
